import { AppDispatch } from '@common/Checkout/shared/types';
import { get } from '@common/Http';
import { API_SEARCH_RESULT_PAGE } from '@routes';
import { DEFAULT_DEPARTMENT_ID } from '../constants';
import { addRequestedParamsToUrl } from '../helpers/addRequestedParamsToUrl';
import type {
    FetchFilters, FilterProperty, SRPData,
} from '../types';
import {
    ADD_DEPARTMENT,
    ADD_SEARCH_TERM,
    TOGGLE_FILTER,
    CLEAR_ALL_FILTERS,
    CLEAR_ALL_FILTERS_V2,
    CLEAR_FILTER_SECTION,
    CLEAR_FILTER_SECTION_V2,
    FETCH_FILTERS_REQUEST,
    FETCH_FILTERS_SUCCESS,
    FETCH_FILTERS_FAILURE,
    TOGGLE_FILTER_SELECTION,
    TOGGLE_SORT_MODAL_VISIBILITY,
    TOGGLE_INFO_CARD_VISIBILITY,
    UPDATE_DEPARTMENT_SELECTION,
    UPDATE_FILTER_SELECTION,
} from './constants';

const addFilterDepartment = (filterProperty: string, value: number | null, isDepartmentAll: boolean) => ({
    type: ADD_DEPARTMENT,
    payload: { filterProperty, value, isDepartmentAll },
});

const addFilterAction = (filterProperty: string, value: number[] | number) => ({
    type: TOGGLE_FILTER,
    payload: { filterProperty, value },
});

export const addSearchTerm = (search: string) => ({
    type: ADD_SEARCH_TERM,
    payload: search,
});

const clearAllFiltersAction = () => ({
    type: CLEAR_ALL_FILTERS,
});

const clearAllFiltersV2Action = () => ({
    type: CLEAR_ALL_FILTERS_V2,
});

const clearFilterSectionAction = (property: string) => ({
    type: CLEAR_FILTER_SECTION,
    payload: property,
});

const clearSectionV2Action = (property: string | string[]) => ({
    type: CLEAR_FILTER_SECTION_V2,
    payload: property,
});

export const fetchFiltersRequest = () => ({
    type: FETCH_FILTERS_REQUEST,
});

export const fetchFiltersSuccess = (filters: SRPData) => ({
    type: FETCH_FILTERS_SUCCESS,
    payload: filters,
});

export const fetchFiltersFailure = (error: any) => ({
    type: FETCH_FILTERS_FAILURE,
    payload: error,
});

export const updateDepartmentSelection = (filterProperty: string, id: number | null) => ({
    type: UPDATE_DEPARTMENT_SELECTION,
    payload: { filterProperty, id },
});

export const updateFilterSelection = (filterProperty: string, id: number[] | number) => ({
    type: UPDATE_FILTER_SELECTION,
    payload: { filterProperty, id },
});

export const toggleFilterSelection = (filterProperty: string, id: number[] | number | null) => ({
    type: TOGGLE_FILTER_SELECTION,
    payload: { filterProperty, id },
});

export const toggleSortModalVisibility = () => ({
    type: TOGGLE_SORT_MODAL_VISIBILITY,
});

export const toggleInfoCardVisibility = () => ({
    type: TOGGLE_INFO_CARD_VISIBILITY,
});

export const handleFiltersRequest = (requestParameters: FetchFilters = {}, isNewSRPFeatureActive: boolean = false) => async (
    dispatch: AppDispatch, getState: () => any,
) => {
    dispatch(fetchFiltersRequest());
    const urlParams = new URLSearchParams(window.location.search);
    const searchKeyFromUrl = urlParams.get('search_key');
    const decodedSearchKeyFromUrl = decodeURIComponent(searchKeyFromUrl ?? '');

    try {
        const state = getState();
        const { indexableResult, searchKey: searchKeyFromState } = state.searchResultPage;
        const hasIndexableResults = Object.keys(indexableResult || []).length;
        const searchKey = hasIndexableResults ? searchKeyFromState : decodedSearchKeyFromUrl;
        const currentFilters: FilterProperty = state.searchResultPage.property;
        const reducedCurrentFilters = Object
            .entries(currentFilters)
            .filter(([, value]) => (Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined))
            .reduce((accumulator: any, item) => {
                const [itemKey, itemValue] = item;

                accumulator[itemKey] = itemValue;

                return accumulator;
            }, {});

        const requestParams = Object
            .entries({
                ...reducedCurrentFilters,
                page: 1,
                ...requestParameters,
                search_key: searchKey,
            })
            .filter(([, value]) => (Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined))
            .reduce((accumulator: any, item) => {
                const [itemKey, itemValue] = item;

                accumulator[itemKey] = itemValue;

                return accumulator;
            }, {});

        if (isNewSRPFeatureActive && !hasIndexableResults) {
            addRequestedParamsToUrl(requestParams);
        }

        const response: SRPData = await get(
            API_SEARCH_RESULT_PAGE,
            requestParams,
        );

        dispatch(fetchFiltersSuccess(response));
    } catch (error) {
        dispatch(fetchFiltersFailure(error as any));
    }
};

export const handleFiltersRequestV2 = () => async (
    dispatch: AppDispatch, getState: () => any,
) => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchKeyFromUrl = urlParams.get('search_key');
    const decodedSearchKeyFromUrl = decodeURIComponent(searchKeyFromUrl ?? '');

    try {
        const state = getState();
        const { indexableResult, searchKey: searchKeyFromState } = state.searchResultPage;
        const hasIndexableResults = Object.keys(indexableResult || []).length;
        const searchKey = hasIndexableResults ? searchKeyFromState : decodedSearchKeyFromUrl;
        const currentFilters: FilterProperty = state.searchResultPage.filters.active;
        const isDefaultDepartmentAll = currentFilters?.departments === DEFAULT_DEPARTMENT_ID;
        const requestParams = {
            ...currentFilters,
            page: 1,
            search_key: searchKey,
        };

        if (!hasIndexableResults && !isDefaultDepartmentAll) {
            addRequestedParamsToUrl(requestParams);
        }

        const response = await get(
            API_SEARCH_RESULT_PAGE,
            requestParams,
        );

        dispatch(fetchFiltersSuccess(response));
    } catch (error) {
        dispatch(fetchFiltersFailure(error as any));
    }
};

export const updatePagination = (page: number, isNewSRPFeatureActive: boolean) => async (dispatch: AppDispatch) => {
    dispatch(handleFiltersRequest({ page }, isNewSRPFeatureActive));
};

export const addDepartment = (
    filterProperty: string, id: number | null, isNewSRPFeatureActive: boolean, isDepartmentAll: boolean,
) => (dispatch: AppDispatch) => {
    const filtersParam = (id !== null) ? { [filterProperty]: id } : {};

    dispatch(addFilterDepartment(filterProperty, id, isDepartmentAll));
    dispatch(handleFiltersRequest(filtersParam, isNewSRPFeatureActive));
};

export const addFilter = (
    filterProperty: string, id: number[] | number, isNewSRPFeatureActive: boolean,
) => (dispatch: AppDispatch) => {
    dispatch(addFilterAction(filterProperty, id));
    dispatch(handleFiltersRequest({ [filterProperty]: id }, isNewSRPFeatureActive));
};

export const applyFilterChanges = (
    filterProperty: string, id: number[] | number,
) => (dispatch: AppDispatch) => {
    const isDefaultDepartmentAll = id === DEFAULT_DEPARTMENT_ID;

    if (!isDefaultDepartmentAll) {
        dispatch(toggleFilterSelection(filterProperty, id));
        dispatch(handleFiltersRequestV2());
    }
};

export const clearAllFilters = (isNewSRPFeatureActive: boolean) => (dispatch: AppDispatch) => {
    dispatch(clearAllFiltersAction());
    dispatch(handleFiltersRequest({}, isNewSRPFeatureActive));
};

export const clearAllFiltersV2 = () => (dispatch: AppDispatch) => {
    dispatch(clearAllFiltersV2Action());
    dispatch(handleFiltersRequestV2());
};

export const clearFilterSection = (property: string, isNewSRPFeatureActive: boolean) => (dispatch: AppDispatch) => {
    dispatch(clearFilterSectionAction(property));
    dispatch(handleFiltersRequest({}, isNewSRPFeatureActive));
};

export const clearFilterSectionV2 = (property: string | string[]) => (dispatch: AppDispatch) => {
    dispatch(clearSectionV2Action(property));
    dispatch(handleFiltersRequestV2());
};
